import React, { useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';

import { Button, Checkbox, Form, Input } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';

import { AUTH_TYPES } from '@shared/pages/Login/constants';
import LoginWrapper from '@shared/pages/Login/LoginWrapper';
import { domainCompanyid } from '@shared/utils/domainCompanyId';
import { validatePassword } from '@shared/utils/password-patterns';

import classNames from 'classnames';
import find from 'lodash/find';
import get from 'lodash/get';

import css from './Login.module.scss';

const RegistrationPage = () => {
  const { t } = useTranslation('registration');

  const {
    company,
    registerUserRecaptcha, // Example additional context values
    providersTypes,
  } = useOutletContext();

  const ldapLogin = providersTypes.includes(AUTH_TYPES.LDAP);

  const isAbsolute = company && company?.id === domainCompanyid.ABSOLUTE;

  const [domainsInformation, setDomainsInformation] = useState({});
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [isMasked, setIsMasked] = useState(true);

  const handleSubmit = formData => {
    if (!isAbsolute) {
      registerUserRecaptcha(formData.email, formData.newPassword);
    } else {
      registerUserRecaptcha(formData.email, formData.newPassword, formData.newsletter);
    }
  };
  const isKampus = document.location.hostname === 'td.kampus.team';

  useLayoutEffect(() => {
    const emailDomains = get(find(company?.providers, { type: 'INTERNAL' }), 'domains', []);

    setDomainsInformation({
      emailDomains,
      showDomainsError: emailDomains.length > 0 && t('domains'),
    });
  }, []);

  return (
    <LoginWrapper type='registr'>
      <Form className={css['LoginPage-form']} onFinish={handleSubmit}>
        <Form.Item
          colon={false}
          name='email'
          className={css['LoginPage-form-item']}
          validateFirst={true}
          rules={[
            {
              required: true,
              message: t('mailRequired'),
            },
            {
              type: 'email',
              message: t('checkMail'),
            },
            {
              message: domainsInformation?.showDomainsError,
              validator: (_, value) => {
                if (value) {
                  const indexEmail = value.indexOf('@');
                  const subEmail = value.substring(indexEmail + 1);
                  if (
                    domainsInformation?.emailDomains.length === 0 ||
                    domainsInformation?.emailDomains.findIndex(item => item === subEmail) !== -1
                  ) {
                    return Promise.resolve();
                  }
                  return Promise.reject();
                } else {
                  return Promise.resolve();
                }
              },
            },
          ]}
        >
          <Input placeholder={t('mail')} />
        </Form.Item>
        <Form.Item
          colon={false}
          name='newPassword'
          className={css['LoginPage-form-item']}
          validateFirst={true}
          rules={[
            {
              required: true,
              message: t('passwordRequired'),
            },
            {
              min: 12,
              message: t('passwordLength'),
            },
            {
              validator: (_, value) => {
                if (
                  validatePassword(ldapLogin, true, value, res => {
                    return res >= 3;
                  })
                ) {
                  return Promise.resolve();
                }
                return Promise.reject(ldapLogin ? new Error(t('passwordWrongLdap')) : new Error(t('passwordWrong')));
              },
            },
          ]}
        >
          {ldapLogin ? (
            <div className={css['LoginPage-form-item-ldapPassword-block']}>
              <Input
                className={classNames(css['LoginPage-form-item-ldapPassword'], {
                  [css['LoginPage-form-item-ldapPassword-hide']]: isMasked,
                })}
                type='text'
                placeholder={t('password')}
                addonAfter={
                  <Button
                    className={css['LoginPage-form-item-ldapPassword__toggle-button']}
                    onClick={() => setIsMasked(!isMasked)}
                    icon={isMasked ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  />
                }
              />
            </div>
          ) : (
            <Input.Password placeholder={t('password')} className={css['LoginPage-form-item-password']} />
          )}
        </Form.Item>
        {isKampus && (
          <Form.Item className={css['LoginPage-form-checkbox']}>
            <Checkbox checked={acceptPrivacy} onChange={() => setAcceptPrivacy(!acceptPrivacy)} />
            &nbsp; Нажимая на кнопку, вы даете согласие на обработку персональных данных, а также соглашаетесь с &nbsp;
            <a href='https://kampus.team/privacy'>политикой конфиденциальности</a>
          </Form.Item>
        )}

        {isAbsolute && (
          <>
            <Form.Item className={css['LoginPage-form-checkbox']}>
              <div className={css['LoginPage-form-checkbox-wrapper']}>
                <Checkbox checked={acceptPrivacy} onChange={() => setAcceptPrivacy(!acceptPrivacy)} />
                <div className={css['LoginPage-form-checkbox-text']}>
                  {t('consentText1')} &nbsp;
                  <a
                    href='https://kampus.team/absolut/processing_policy_personal_data.pdf'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {t('personalDataPolicy')}
                  </a>
                  &nbsp; {t('consentText2')} &nbsp;
                  <a
                    href='https://kampus.team/absolut/consent_to_the_processing_of_personal_data.pdf'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {t('personalDataConsent')}
                  </a>
                </div>
              </div>
            </Form.Item>

            <Form.Item
              name='newsletter'
              valuePropName='checked'
              initialValue={false}
              className={css['LoginPage-form-checkbox']}
            >
              <div className={css['LoginPage-form-checkbox-wrapper']}>
                <Checkbox />
                <div className={css['LoginPage-form-checkbox-text']}>
                  {t('consentText3')} &nbsp;
                  <a
                    href='https://kampus.team/absolut/consent_to_receive_advertising_mailings.pdf'
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {t('advertisingConsent')}
                  </a>
                </div>
              </div>
            </Form.Item>
          </>
        )}

        <Form.Item className={css['LoginPage-form-google']}>
          <Button
            size='large'
            type={'primary'}
            htmlType='submit'
            disabled={isKampus || isAbsolute ? !acceptPrivacy : false}
            data-qa='registrationBtn'
          >
            {t('singUp')}
          </Button>
        </Form.Item>
      </Form>
    </LoginWrapper>
  );
};

export default RegistrationPage;
